<template>
  <div class="fee-receipt-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('feeReceipt.list.title')" :subtitle="$t('feeReceipt.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="feeReceiptManageInfo.conditions.receiptId" clearable class="w-full"
            :placeholder="$t('feeReceipt.search.receiptId')" @keyup.enter.native="_queryFeeReceiptMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="feeReceiptManageInfo.conditions.roomId" clearable class="w-full"
            :placeholder="$t('feeReceipt.search.roomId')" @keyup.enter.native="_queryFeeReceiptMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="feeReceiptManageInfo.conditions.objType" clearable class="w-full"
            :placeholder="$t('feeReceipt.search.objType')">
            <el-option :label="$t('feeReceipt.search.all')" value="" />
            <el-option :label="$t('feeReceipt.search.houseFee')" value="3333" />
            <el-option :label="$t('feeReceipt.search.parkingFee')" value="6666" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-date-picker v-model="feeReceiptManageInfo.conditions.qstartTime" type="date" class="w-full"
            :placeholder="$t('feeReceipt.search.startTime')" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-date-picker v-model="feeReceiptManageInfo.conditions.qendTime" type="date" class="w-full"
            :placeholder="$t('feeReceipt.search.endTime')" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryFeeReceiptMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetFeeReceiptMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listFeeReceipts">
        <template #left>
          <span class="ds-panel__title">{{ $t('feeReceipt.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="feeReceiptManageInfo.feeReceipts" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border :empty-description="$t('feeReceipt.page.empty')"
        @retry="_listFeeReceipts">
        <el-table-column prop="receiptId" :label="$t('feeReceipt.table.receiptId')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.receiptId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="receiptCode" :label="$t('feeReceipt.table.receiptCode')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.receiptCode || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="objType" :label="$t('feeReceipt.table.objType')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.objType === '3333' ? $t('feeReceipt.search.houseFee') : $t('feeReceipt.search.parkingFee') }}
          </template>
        </el-table-column>
        <el-table-column prop="feeName" :label="$t('feeReceipt.table.feeName')" align="left" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.feeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="roomName" :label="$t('feeReceipt.table.roomName')" align="center" width="150">
          <template slot-scope="scope">
            {{ scope.row.roomName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="payObjName" :label="$t('feeReceipt.table.payObjName')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.payObjName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="carNum" :label="$t('feeReceipt.table.carNum')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.carNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="amount" :label="$t('feeReceipt.table.amount')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('feeReceipt.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            {{ formatDateTimeSec(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="190">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="_printFeeReceipt(scope.row)">
              {{ $t('feeReceipt.button.reprint') }}
            </el-button>
            <el-button type="text" size="small" @click="_printFeeSmallReceipt(scope.row)">
              {{ $t('feeReceipt.button.reprintSmall') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>
  </div>
</template>

<script>
import { queryFeeReceipt, listFeePrintPage } from '@/api/fee/feeReceiptApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'FeeReceiptList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      feeReceiptManageInfo: {
        feeReceipts: [],
        moreCondition: false,
        printUrl: '/#/pages/property/printPayFee',
        conditions: {
          objType: '',
          storeName: '',
          objId: '',
          month: '',
          qstartTime: '',
          qendTime: '',
          type: '',
          roomId: '',
          communityId: getCommunityId(),
          receiptId: '',
          page: 1,
          row: 10
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this._listFeeReceipts()
    this._listFeePrintPages()
  },
  methods: {
    async _listFeeReceipts() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.feeReceiptManageInfo.conditions,
          page: this.page.current,
          row: this.page.size
        }
        const { data, total } = await queryFeeReceipt(params)
        this.feeReceiptManageInfo.feeReceipts = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('feeReceipt.page.loadFailed')
        this.$message.error(this.$t('feeReceipt.fetchError'))
      } finally {
        this.loading = false
      }
    },
    async _listFeePrintPages() {
      try {
        const params = {
          page: 1,
          row: 1,
          state: 'T',
          communityId: getCommunityId()
        }
        const { data } = await listFeePrintPage(params)
        if (data && data.length > 0) {
          this.feeReceiptManageInfo.printUrl = data[0].url
        }
      } catch (error) {
        console.error('请求失败:', error)
      }
    },
    _queryFeeReceiptMethod() {
      this.page.current = 1
      this._listFeeReceipts()
    },
    _resetFeeReceiptMethod() {
      this.feeReceiptManageInfo.conditions = {
        ...this.feeReceiptManageInfo.conditions,
        objType: '',
        roomId: '',
        receiptId: '',
        qstartTime: '',
        qendTime: ''
      }
      this._listFeeReceipts()
    },
    _printFeeReceipt(receipt) {
      window.open(`${this.feeReceiptManageInfo.printUrl}?receiptId=${receipt.receiptId}`)
    },
    _printFeeSmallReceipt(receipt) {
      window.open(`/#/pages/property/printSmallPayFee?receiptIds=${receipt.receiptId}`)
    },
    _moreCondition() {
      this.feeReceiptManageInfo.moreCondition = !this.feeReceiptManageInfo.moreCondition
    },
    handleSizeChange(val) {
      this.page.size = val
      this._listFeeReceipts()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listFeeReceipts()
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-receipt-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
